<template>
  <view-page
    :title="title"
    :delete-button="canBeDeleted"
    :delete-dialog-title="$t('DeleteItem')"
    :delete-dialog-content="
      $t('DeleteConfirmation') + ' ' + $tc('Template', 1) + ' ' + title + '?'
    "
    :loading="loading"
    permission-name="asset_reservation_template"
    @editItem="editItem"
    @deleteItem="deleteItem"
  >
    <template v-slot:leftSide>
      <view-page-data-field
        v-if="fields"
        v-for="field in fields"
        :key="field.template"
        :icon="field.icon"
        :label="field.label"
        :data="field.data"
      />
    </template>

    <template v-slot:rightSide>
      <reservation-template-fields-list
        :request-fields="requestFields"
        action-type="view"
      />
    </template>
  </view-page>
</template>

<script>
import { mapActions } from "vuex";
import ViewPage from "@/components/Management/ViewPage/ViewPage";
import ViewPageDataField from "@/components/Management/ViewPage/ViewPageDataField";
import ReservationTemplateFieldsList from "@/components/AssetReservationManagement/Templates/ReservationTemplateFieldsList";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";

export default {
  name: "AssetReservationManagementTemplateDetails",

  components: {
    ReservationTemplateFieldsList,
    ViewPageDataField,
    ViewPage
  },

  mixins: [managementDatatableMixin],

  data() {
    return {
      /**
       * Selected template
       */
      template: null,
      /**
       * Define if the page is loading
       */
      loading: false
    };
  },

  computed: {
    /**
     * uuid of the selected template that is defined in the route
     * @return {String}
     */
    templateUuid() {
      return this.$route.params.uuid;
    },

    /**
     * Determines the fields to be displayed on the left side of the page
     */
    fields() {
      if (this.template) {
        return [
          {
            icon: "label",
            label: this.$i18n.tc("Label", 1),
            data: this.template.art_label
          },
          {
            icon: "business",
            label: this.$i18n.tc("Owner", 1),
            data: this.template.owner_name
          },
          {
            icon: "layers",
            label: this.$i18n.tc("Layer", 2),
            data: this.getPossibleLayers(this.template)
          },
          {
            icon: "power_settings_new",
            label: this.$i18n.t("Active"),
            data: this.template.art_active
              ? this.$i18n.t("Yes")
              : this.$i18n.t("No")
          },
          {
            icon: "assignment_turned_in",
            label: this.$i18n.t("Status"),
            data: this.getPossibleStatus(this.template.art_status)
          },
          {
            icon: "list",
            label: this.$i18n.t("Index"),
            data: this.template.art_index
          },
          {
            icon: "mdi-counter",
            label: this.$i18n.t("NumberReservations"),
            data: this.template.active_asset_reservations_count
          }
        ];
      }
      return null;
    },

    /**
     * Determines the title of the page
     * @return {String}
     */
    title() {
      return this.template
        ? this.template.art_label
        : this.$i18n.tc("Template", 1);
    },

    /**
     * Determines whether the user can remove item
     * @return {boolean}
     */
    canBeDeleted() {
      return (
        this.template && this.template.active_asset_reservations_count === 0 && this.userHasPermissionTo('destroy')
      );
    },

    /**
     * Determines the request fields
     * @return {String}
     */
    requestFields() {
      return this.template ? this.template.request_fields : [];
    }
  },

  watch: {
    /**
     * Whenever the uuid of the template changes it calls the getAssetReservationTemplate method
     */
    templateUuid() {
      this.getAssetReservationTemplate();
    }
  },

  mounted() {
    this.getAssetReservationTemplate();
  },

  methods: {
    ...mapActions([
      "fetchAssetReservationTemplateByUUID",
      "deleteAssetReservationTemplate",
      "GET_LIST"
    ]),

    /**
     * Get template by the uuid that is defined in the current page route
     * @return {Promise<void>}
     */
    async getAssetReservationTemplate() {
      if (this.templateUuid) {
        this.loading = true;
        let appends = [
          "active_asset_reservations_count",
          "owner_name",
          "request_fields"
        ];

        try {
          this.template = await this.fetchAssetReservationTemplateByUUID({
            templateUUID: this.templateUuid,
            appends: appends.join(",")
          });
        } catch (e) {
          await this.$router.replace("/error");
          console.log(e);
        } finally {
          this.loading = false;
        }
      }
    },

    /**
     * Get the translation of the layers
     *
     * @param {String} template
     * @return {String} Returns the translation of the layers
     */
    getPossibleLayers(template) {
      if (template) {
        let layers = template.art_accepted_layers;

        if (layers === "all") {
          return "All";
        } else {
          layers = layers.split(",");

          let layerNames = [];
          for (const layer of layers) {
            layerNames.push(this.$store.getters.getLayerTranslationKey(layer));
          }

          return layerNames.join(", ");
        }
      } else {
        return null;
      }
    },
    /**
     * Get status label from list_avancement list
     * @param statusCode
     * @returns {string|null|*|string}
     */
    getPossibleStatus(statusCode) {
      if (statusCode) {
        const statusArray = statusCode.split(',')
        let status = [];
        let listName = "list_avancement";
        let storedList = this.$store.getters.getListOfOptionsByListName(
          listName,
          [statusCode],
          true
        );
        if(storedList){
          statusArray.forEach(element =>{
            status.push(storedList.find(item => item.code === element));
          })
        }else {
          storedList = this.GET_LIST({
            listName: listName,
            code: [statusCode],
            includeDeleted: true
          });
          status = storedList.find(item => item.code === statusCode);
        }
        if (status){
          let statusString = [];
          status.forEach(item =>{
            const label = item && item.client_label ? item.client_label : item.libelle;
            statusString.push(label);
          })
          return statusString.join(', ');
        }
        else return "-";
      } else return "-"
    },

    /**
     * Push to the page to edit the selected template
     */
    editItem() {
      /**
       * @property {String} name - Name of the route
       * @property {Object} params - Parameters needed for the route path
       */
      this.$router.push({
        name: "reservation-template-edit",
        params: { uuid: this.templateUuid }
      });
    },

    /**
     * Sends a request to delete the selected template and and push it to the templates list page.
     */
    async deleteItem() {
      await this.deleteAssetReservationTemplate(this.templateUuid);
      this.$router.back();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AssetReservationManagement/Templates/ReservationTemplateDetails.vue